import { create } from "zustand";
import { apiClient } from "@/api/client";
import { AxiosError } from "axios";

interface Objective {
  id: string;
  name: string;
  desc?: string;
  media_agency_id: string;
  media_agency_name?: string;
}

interface ObjectiveResponse {
  data: Objective[];
}

interface ObjectiveFormData {
  title: string;
  description: string;
  media_agency_id: string;
  isLoading: boolean;
}

interface ObjectiveState {
  objectives: Objective[] | null;
  sortField: string;
  formData: ObjectiveFormData;
  setFormData: (data: Partial<ObjectiveFormData>) => void;
  resetFormData: () => void;
  getObjectives: (sort?: string) => Promise<void>;
  addObjective: () => Promise<void>;
  editObjective: (id: string) => Promise<void>;
  deleteObjective: (id: string) => Promise<void>;
  setFormDataForEdit: (objective: Objective) => void;
  setObjectives: (objectives: Objective[]) => void;
}

const initialFormData: ObjectiveFormData = {
  title: "",
  description: "",
  media_agency_id: "",
  isLoading: false,
};

const useObjectiveStore = create<ObjectiveState>((set, get) => ({
  objectives: null,
  sortField: '',

  formData: { ...initialFormData },

  resetFormData: () => set({ formData: { ...initialFormData } }),

  setFormData: (data) =>
    set((state) => ({
      formData: {
        ...state.formData,
        ...data,
      },
    })),

  setFormDataForEdit: (objective) => {
    set({
      formData: {
        title: objective.name,
        description: objective.desc || "",
        media_agency_id: objective.media_agency_id || "",
        isLoading: false,
      },
    });
  },

  setObjectives: (objectives: Objective[]) => set({ objectives }),

  getObjectives: async (sort?: string) => {
    try {
      const params = sort ? { sort } : {};
      const response = await apiClient.get<ObjectiveResponse>("/objectives", { params });
      set({ objectives: response.data.data, sortField: sort || '' });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.error || "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  addObjective: async () => {
    const { formData, objectives, setFormData } = get();
    setFormData({ isLoading: true });

    try {
      const response = await apiClient.post("/objectives", {
        name: formData.title,
        desc: formData.description,
        media_agency_id: formData.media_agency_id,
      });

      const newObjective = response.data;
      get().setObjectives([...(objectives ?? []), newObjective]);

      setFormData({ ...initialFormData });
    } catch (error) {
      setFormData({ isLoading: false });
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.error || "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  editObjective: async (id) => {
    const { formData, objectives, setFormData } = get();
    setFormData({ isLoading: true });

    try {
      const response = await apiClient.put(`/objectives/${id}`, {
        name: formData.title,
        desc: formData.description,
        media_agency_id: formData.media_agency_id,
      });

      const updatedObjective = response.data;
      const updatedObjectives = (objectives ?? []).map((obj) =>
        obj.id === id ? updatedObjective : obj
      );
      get().setObjectives(updatedObjectives);

      setFormData({ ...initialFormData });
    } catch (error) {
      setFormData({ isLoading: false });
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.error || "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  deleteObjective: async (id) => {
    try {
      await apiClient.delete(`/objectives/${id}`);
      const updatedObjectives = (get().objectives ?? []).filter(
        (obj) => obj.id !== id
      );
      get().setObjectives(updatedObjectives);
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.error || "Something went wrong";
      throw new Error(errorMessage);
    }
  },
}));

export default useObjectiveStore;
